import { supabase } from '../lib/supabase';

// ============================================================
// TRAINING PROGRAMS SERVICE (Partner-submitted programs)
// ============================================================
export const trainingProgramsService = {
  async getAll(filters = {}) {
    let query = supabase
      ?.from('training_programs')
      ?.select('*, partners(legal_name, brand_name, tier)')
      ?.order('created_at', { ascending: false });
    if (filters?.status && filters?.status !== 'all') {
      query = query?.eq('program_status', filters?.status);
    }
    if (filters?.partnerId) {
      query = query?.eq('delivering_partner_id', filters?.partnerId);
    }
    if (filters?.category && filters?.category !== 'all') {
      query = query?.eq('category', filters?.category);
    }
    if (filters?.format && filters?.format !== 'all') {
      query = query?.eq('format', filters?.format);
    }
    if (filters?.search) {
      query = query?.ilike('program_name', `%${filters?.search}%`);
    }
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getById(id) {
    const { data, error } = await supabase
      ?.from('training_programs')
      ?.select('*, partners(legal_name, brand_name, tier), program_sessions(*), program_enrollments(*), program_feedback(*)')
      ?.eq('id', id)
      ?.single();
    if (error) throw error;
    return data;
  },

  async create(programData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('training_programs')
      ?.insert({ ...programData, created_by: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      ?.from('training_programs')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async remove(id) {
    const { error } = await supabase?.from('training_programs')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};

// ============================================================
// PROGRAM SESSIONS SERVICE
// ============================================================
export const programSessionsService = {
  async getByProgram(programId) {
    const { data, error } = await supabase
      ?.from('program_sessions')
      ?.select('*')
      ?.eq('program_id', programId)
      ?.order('session_date', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async getUpcoming(daysAhead = 60) {
    const futureDate = new Date();
    futureDate?.setDate(futureDate?.getDate() + daysAhead);
    const { data, error } = await supabase
      ?.from('program_sessions')
      ?.select('*, training_programs(program_name, category, format, delivering_partner_id, partners(brand_name, legal_name))')
      ?.gte('session_date', new Date()?.toISOString()?.split('T')?.[0])
      ?.lte('session_date', futureDate?.toISOString()?.split('T')?.[0])
      ?.eq('session_status', 'scheduled')
      ?.order('session_date', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async getAllWithPrograms() {
    const { data, error } = await supabase
      ?.from('program_sessions')
      ?.select('*, training_programs(program_name, category, format, delivering_partner_id, partners(brand_name, legal_name))')
      ?.order('session_date', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async create(sessionData) {
    const { data, error } = await supabase
      ?.from('program_sessions')
      ?.insert(sessionData)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      ?.from('program_sessions')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  }
};

// ============================================================
// PROGRAM ENROLLMENTS SERVICE
// ============================================================
export const programEnrollmentsService = {
  async getByProgram(programId) {
    const { data, error } = await supabase
      ?.from('program_enrollments')
      ?.select('*')
      ?.eq('program_id', programId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async getAll(filters = {}) {
    let query = supabase
      ?.from('program_enrollments')
      ?.select('*, training_programs(program_name, delivering_partner_id, partners(brand_name, legal_name))')
      ?.order('created_at', { ascending: false });
    if (filters?.programId) query = query?.eq('program_id', filters?.programId);
    if (filters?.status && filters?.status !== 'all') query = query?.eq('attendance_status', filters?.status);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(enrollmentData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('program_enrollments')
      ?.insert({ ...enrollmentData, enrolled_by: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      ?.from('program_enrollments')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async remove(id) {
    const { error } = await supabase?.from('program_enrollments')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};

// ============================================================
// PROGRAM MATERIALS SERVICE
// ============================================================
export const programMaterialsService = {
  async getByProgram(programId) {
    const { data, error } = await supabase
      ?.from('program_materials')
      ?.select('*')
      ?.eq('program_id', programId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async create(materialData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('program_materials')
      ?.insert({ ...materialData, uploaded_by: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async remove(id) {
    const { error } = await supabase?.from('program_materials')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};

// ============================================================
// PROGRAM FEEDBACK SERVICE
// ============================================================
export const programFeedbackService = {
  async getByProgram(programId) {
    const { data, error } = await supabase
      ?.from('program_feedback')
      ?.select('*')
      ?.eq('program_id', programId)
      ?.order('submitted_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async getAverageRating(programId) {
    const { data, error } = await supabase
      ?.from('program_feedback')
      ?.select('rating')
      ?.eq('program_id', programId);
    if (error) throw error;
    if (!data?.length) return 0;
    const avg = data?.reduce((sum, r) => sum + (r?.rating || 0), 0) / data?.length;
    return Math.round(avg * 10) / 10;
  },

  async create(feedbackData) {
    const { data, error } = await supabase
      ?.from('program_feedback')
      ?.insert(feedbackData)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  }
};

// ============================================================
// TRAINING REQUESTS SERVICE
// ============================================================
export const trainingRequestsService = {
  async getAll(filters = {}) {
    let query = supabase
      ?.from('training_requests')
      ?.select('*, partners(legal_name, brand_name, tier)')
      ?.order('created_at', { ascending: false });
    if (filters?.status && filters?.status !== 'all') {
      query = query?.eq('request_status', filters?.status);
    }
    if (filters?.type && filters?.type !== 'all') {
      query = query?.eq('request_type', filters?.type);
    }
    if (filters?.partnerId) {
      query = query?.eq('partner_id', filters?.partnerId);
    }
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(requestData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('training_requests')
      ?.insert({ ...requestData, requested_by: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('training_requests')
      ?.update({ ...updates, handled_by: user?.id, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async remove(id) {
    const { error } = await supabase?.from('training_requests')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};

// ============================================================
// ECITY CERTIFICATIONS SERVICE
// ============================================================
export const ecityCertificationsService = {
  async getAll(filters = {}) {
    let query = supabase
      ?.from('ecity_certifications')
      ?.select('*, partners(legal_name, brand_name)')
      ?.order('issue_date', { ascending: false });
    if (filters?.status && filters?.status !== 'all') {
      query = query?.eq('cert_status', filters?.status);
    }
    if (filters?.partnerId) {
      query = query?.eq('issued_by_partner_id', filters?.partnerId);
    }
    if (filters?.search) {
      query = query?.ilike('issued_to_name', `%${filters?.search}%`);
    }
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getExpiring(daysAhead = 30) {
    const futureDate = new Date();
    futureDate?.setDate(futureDate?.getDate() + daysAhead);
    const { data, error } = await supabase
      ?.from('ecity_certifications')
      ?.select('*, partners(legal_name, brand_name)')
      ?.lte('expiry_date', futureDate?.toISOString()?.split('T')?.[0])
      ?.gte('expiry_date', new Date()?.toISOString()?.split('T')?.[0])
      ?.eq('cert_status', 'active')
      ?.order('expiry_date', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async create(certData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('ecity_certifications')
      ?.insert({ ...certData, issued_by_user: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      ?.from('ecity_certifications')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async remove(id) {
    const { error } = await supabase?.from('ecity_certifications')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  async uploadDocument(certId, file) {
    const filePath = `ecity-certs/${certId}_${Date.now()}_${file?.name}`;
    const { error: uploadError } = await supabase?.storage
      ?.from('partner-documents')
      ?.upload(filePath, file, { upsert: true });
    if (uploadError) throw uploadError;
    const { data, error } = await supabase
      ?.from('ecity_certifications')
      ?.update({ document_path: filePath, document_name: file?.name })
      ?.eq('id', certId)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async getDocumentUrl(filePath) {
    const { data, error } = await supabase?.storage
      ?.from('partner-documents')
      ?.createSignedUrl(filePath, 3600);
    if (error) throw error;
    return data?.signedUrl;
  }
};
